import {
  faArrowUpRightFromSquare,
  faCopy,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link } from '@tanstack/react-router';
import { TRANSLATION_LOCALES } from '@/api/(admin)/translations/types';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SheetFooter } from '@/components/ui/sheet';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { useTranslator } from '@/hooks/useTranslator';
import { useTranslationKeyEditor } from './hooks/useTranslationKeyEditor';

export function TranslationKeyEditor({
  id,
  onNavigate,
}: {
  /** `namespace:key` */
  id: string;
  onNavigate: () => void;
}) {
  const { getTranslation: t } = useTranslator({ page: 'translationInspector' });
  const { getTranslation: tAdmin } = useTranslator({
    page: 'managementTranslations',
  });
  const editor = useTranslationKeyEditor(id);

  return (
    <>
      <div className='flex flex-1 flex-col gap-4 overflow-y-auto px-6 py-4'>
        <div className='flex items-center gap-2'>
          <code className='min-w-0 flex-1 truncate rounded-sm bg-muted px-2 py-1 font-mono text-xs'>
            {id}
          </code>
          <Button variant='outline' size='sm' onClick={editor.copy}>
            <FontAwesomeIcon icon={faCopy} />
            {t('panel.copy')}
          </Button>
          <Button
            variant='outline'
            size='sm'
            render={
              <Link
                to='/management/translations'
                search={{ page: 1, limit: 50, filters: { search: [id] } }}
                onClick={onNavigate}
              />
            }
          >
            <FontAwesomeIcon icon={faArrowUpRightFromSquare} />
            {t('panel.open')}
          </Button>
        </div>

        {editor.isLoading && (
          <div className='flex flex-col gap-3'>
            <Skeleton className='h-14 w-full' />
            <Skeleton className='h-14 w-full' />
          </div>
        )}
        {editor.isError && (
          <Alert variant='destructive' className='py-2.5'>
            <AlertDescription>{t('panel.loadFailed')}</AlertDescription>
          </Alert>
        )}
        {!editor.isLoading && !editor.isError && !editor.translation && (
          <Alert className='py-2.5'>
            <AlertDescription>{t('panel.notFound')}</AlertDescription>
          </Alert>
        )}

        {editor.translation &&
          TRANSLATION_LOCALES.map(locale => (
            <div key={locale} className='flex flex-col gap-1.5'>
              <Label
                htmlFor={`translation-inspector-${locale}`}
                className='flex items-center gap-2'
              >
                {tAdmin(`locales.${locale}`)}
                {editor.translation?.overriddenLocales.includes(locale) && (
                  <Badge variant='secondary'>
                    {tAdmin('columns.overridden')}
                  </Badge>
                )}
              </Label>
              <Textarea
                id={`translation-inspector-${locale}`}
                rows={2}
                value={editor.textFor(locale)}
                onChange={event => editor.setValue(locale, event.target.value)}
              />
            </div>
          ))}
      </div>
      {editor.translation && (
        <SheetFooter className='shrink-0 border-t px-6 py-3'>
          <Button disabled={!editor.canSave} onClick={editor.save}>
            {t('panel.save')}
          </Button>
        </SheetFooter>
      )}
    </>
  );
}
